import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiEndpoints, handleApiError } from '../services/api';
import { useSettings } from '../contexts/SettingsContext';
import { toast } from 'react-hot-toast';
import {
  Plus, Search, RefreshCw, FileText, CreditCard, X, ChevronDown, ChevronRight,
  CheckCircle, Clock, AlertCircle, Ban, Building2, Package, Printer, Filter
} from 'lucide-react';
import Modal from './ui/Modal';

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const cfg = {
    Unpaid: { color: '#ef4444', bg: 'rgba(239,68,68,.12)', icon: <AlertCircle size={12} />, label: 'Non payée' },
    PartiallyPaid: { color: '#f59e0b', bg: 'rgba(245,158,11,.12)', icon: <Clock size={12} />, label: 'Partiellement payée' },
    Paid: { color: '#10b981', bg: 'rgba(16,185,129,.12)', icon: <CheckCircle size={12} />, label: 'Soldée' },
    Cancelled: { color: '#6b7280', bg: 'rgba(107,114,128,.12)', icon: <Ban size={12} />, label: 'Annulée' },
  };
  const s = cfg[status] || cfg.Unpaid;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px',
      borderRadius: 999, background: s.bg, color: s.color, fontSize: 12, fontWeight: 600
    }}>
      {s.icon} {s.label}
    </span>
  );
};

// ─── Currency formatter ────────────────────────────────────────────────────────
const fmt = (n) => new Intl.NumberFormat('fr-DZ', { style: 'currency', currency: 'DZD', maximumFractionDigits: 0 }).format(n ?? 0);

// ─── Main component ────────────────────────────────────────────────────────────
const SupplierInvoiceManagement = () => {
  const { t } = useTranslation();
  const { getBranding } = useSettings();

  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedInvoice, setExpandedInvoice] = useState(null);

  // Filters & Pagination
  const [search, setSearch] = useState('');
  const [filterSupplierId, setFilterSupplierId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Modals & Printable Ticket
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [printInvoice, setPrintInvoice] = useState(null);

  // Create invoice form
  const [invoiceForm, setInvoiceForm] = useState({
    supplierId: '',
    invoiceDate: new Date().toISOString().split('T')[0],
    paymentMethod: '',
    notes: '',
    lineItems: [{ productId: '', description: '', quantity: 1, unitPrice: 0 }]
  });

  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: '',
    reference: '',
    notes: ''
  });

  const [submitting, setSubmitting] = useState(false);

  // ── Fetch data ───────────────────────────────────────────────────────────────
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search) params.search = search;
      if (filterSupplierId) params.supplierId = filterSupplierId;
      if (filterStatus) params.status = filterStatus;
      const res = await apiEndpoints.supplierInvoices.getAll(params);
      let itemsList = res.data.items || [];

      // Sort order: Soldée (Paid) invoices on TOP, then partially paid, then unpaid
      const statusWeight = { Paid: 1, PartiallyPaid: 2, Unpaid: 3, Cancelled: 4 };
      itemsList.sort((a, b) => (statusWeight[a.status] || 99) - (statusWeight[b.status] || 99));

      setInvoices(itemsList);
      setTotalPages(res.data.totalPages || 1);
    } catch (err) {
      handleApiError(err, 'Erreur lors du chargement des factures');
    } finally {
      setLoading(false);
    }
  }, [page, search, filterSupplierId, filterStatus]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await apiEndpoints.supplierInvoices.getSummary();
      setSummary(res.data);
    } catch (err) {
      // Non-critical
    }
  }, []);

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await apiEndpoints.suppliers.getAll();
      setSuppliers(res.data || []);
    } catch (err) {
      // Non-critical
    }
  }, []);

  const fetchInventory = useCallback(async () => {
    try {
      const res = await apiEndpoints.inventory.getAll();
      const rawData = res.data;
      const itemsList = Array.isArray(rawData) ? rawData : (rawData?.items || []);
      setInventoryItems(itemsList);
    } catch (err) {
      // Non-critical
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
    fetchSummary();
  }, [fetchInvoices, fetchSummary]);

  useEffect(() => {
    fetchSuppliers();
    fetchInventory();
  }, [fetchSuppliers, fetchInventory]);

  // ── Line item helpers ─────────────────────────────────────────────────────────
  const addLineItem = () => {
    setInvoiceForm(f => ({ ...f, lineItems: [...f.lineItems, { productId: '', description: '', quantity: 1, unitPrice: 0 }] }));
  };

  const removeLineItem = (idx) => {
    setInvoiceForm(f => ({ ...f, lineItems: f.lineItems.filter((_, i) => i !== idx) }));
  };

  const updateLineItem = (idx, field, value) => {
    setInvoiceForm(f => {
      const items = [...f.lineItems];
      items[idx] = { ...items[idx], [field]: value };
      if (field === 'productId' && value) {
        const product = inventoryItems.find(p => p.id === parseInt(value));
        if (product) {
          items[idx].unitPrice = product.unitCost || 0;
          items[idx].description = product.name;
        }
      }
      return { ...f, lineItems: items };
    });
  };

  const invoiceTotal = invoiceForm.lineItems.reduce((s, li) => s + (parseFloat(li.quantity || 0) * parseFloat(li.unitPrice || 0)), 0);

  // ── Create invoice ────────────────────────────────────────────────────────────
  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    if (!invoiceForm.supplierId) { toast.error('Sélectionnez un fournisseur'); return; }
    if (invoiceForm.lineItems.some(li => !li.quantity || !li.unitPrice)) {
      toast.error('Chaque ligne doit avoir une quantité et un prix'); return;
    }
    setSubmitting(true);
    try {
      await apiEndpoints.supplierInvoices.create({
        supplierId: parseInt(invoiceForm.supplierId),
        invoiceDate: invoiceForm.invoiceDate,
        paymentMethod: invoiceForm.paymentMethod || null,
        notes: invoiceForm.notes || null,
        lineItems: invoiceForm.lineItems.map(li => ({
          productId: li.productId ? parseInt(li.productId) : null,
          description: li.description || null,
          quantity: parseFloat(li.quantity),
          unitPrice: parseFloat(li.unitPrice)
        }))
      });
      toast.success('Facture créée avec succès !');
      setShowCreateModal(false);
      setInvoiceForm({ supplierId: '', invoiceDate: new Date().toISOString().split('T')[0], paymentMethod: '', notes: '', lineItems: [{ productId: '', description: '', quantity: 1, unitPrice: 0 }] });
      fetchInvoices();
      fetchSummary();
    } catch (err) {
      handleApiError(err, 'Erreur lors de la création de la facture');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Add payment ───────────────────────────────────────────────────────────────
  const openPaymentModal = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentForm({ amount: invoice.remainingBalance, paymentDate: new Date().toISOString().split('T')[0], paymentMethod: '', reference: '', notes: '' });
    setShowPaymentModal(true);
  };

  const handleAddPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Montant invalide'); return;
    }
    setSubmitting(true);
    try {
      await apiEndpoints.supplierInvoices.addPayment(selectedInvoice.id, {
        amount: parseFloat(paymentForm.amount),
        paymentDate: paymentForm.paymentDate,
        paymentMethod: paymentForm.paymentMethod || null,
        reference: paymentForm.reference || null,
        notes: paymentForm.notes || null
      });
      toast.success('Paiement enregistré !');
      setShowPaymentModal(false);
      fetchInvoices();
      fetchSummary();
    } catch (err) {
      handleApiError(err, 'Erreur lors de l\'enregistrement du paiement');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Cancel invoice ─────────────────────────────────────────────────────────────
  const handleCancelInvoice = async (invoice) => {
    if (!window.confirm(`Annuler la facture ${invoice.invoiceNumber} ? Le stock sera restitué.`)) return;
    try {
      await apiEndpoints.supplierInvoices.cancel(invoice.id);
      toast.success('Facture annulée');
      fetchInvoices();
      fetchSummary();
    } catch (err) {
      handleApiError(err, 'Erreur lors de l\'annulation');
    }
  };

  // ── Print ticket / invoice ─────────────────────────────────────────────────────
  const triggerPrint = (invoice) => {
    setPrintInvoice(invoice);
    setTimeout(() => {
      window.print();
    }, 200);
  };

  // ── Reporting Calculations ────────────────────────────────────────────────────
  const grandTotalPurchases = invoices.reduce((acc, i) => acc + (i.totalAmount || 0), 0);
  const grandTotalPaid = invoices.reduce((acc, i) => acc + (i.paidAmount || 0), 0);
  const grandTotalRemaining = invoices.reduce((acc, i) => acc + (i.remainingBalance || 0), 0);

  // Group reporting by supplier
  const supplierReporting = suppliers.map(sup => {
    const supInvoices = invoices.filter(i => i.supplierId === sup.id);
    const total = supInvoices.reduce((a, b) => a + (b.totalAmount || 0), 0);
    const paid = supInvoices.reduce((a, b) => a + (b.paidAmount || 0), 0);
    const remaining = supInvoices.reduce((a, b) => a + (b.remainingBalance || 0), 0);
    return {
      supplier: sup,
      invoiceCount: supInvoices.length,
      total,
      paid,
      remaining
    };
  }).filter(r => r.invoiceCount > 0);

  // ── Styles ────────────────────────────────────────────────────────────────────
  const styles = {
    container: { padding: '24px', maxWidth: '100%' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
    title: { fontSize: 24, fontWeight: 700, color: '#1e293b', margin: 0 },
    summaryGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 },
    card: { background: '#fff', borderRadius: 12, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,.08)', border: '1px solid #e2e8f0' },
    cardLabel: { fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' },
    cardValue: { fontSize: 22, fontWeight: 700 },
    filterBar: { display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' },
    input: { padding: '9px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 14, outline: 'none', background: '#fff' },
    btn: (variant = 'primary') => ({
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 8,
      fontWeight: 600, fontSize: 14, cursor: 'pointer', border: 'none',
      background: variant === 'primary' ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : variant === 'danger' ? 'rgba(239,68,68,.1)' : variant === 'green' ? 'linear-gradient(135deg,#10b981,#059669)' : '#f1f5f9',
      color: variant === 'primary' || variant === 'green' ? '#fff' : variant === 'danger' ? '#ef4444' : '#475569',
      transition: 'all .2s',
    }),
    table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,.08)', border: '1px solid #e2e8f0' },
    th: { padding: '12px 16px', textAlign: 'left', background: '#f8fafc', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #e2e8f0' },
    td: { padding: '14px 16px', fontSize: 14, color: '#334155', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
  };

  const paymentMethods = ['Espèces', 'Virement', 'Chèque', 'Carte bancaire'];
  const statusOptions = [
    { value: '', label: 'Tous statuts' },
    { value: 'Paid', label: 'Soldée (Payée)' },
    { value: 'PartiallyPaid', label: 'Partiellement payée' },
    { value: 'Unpaid', label: 'Non payée' },
    { value: 'Cancelled', label: 'Annulée' },
  ];

  return (
    <div style={styles.container}>
      {/* Printable Invoice / Ticket View (Hidden on Screen, Visible on Print) */}
      {printInvoice && (
        <div className="hidden print:block p-8 bg-white text-black font-sans">
          <div className="flex justify-between items-center border-b pb-4 mb-6">
            <div>
              {getBranding('club.logo.url') && (
                <img src={getBranding('club.logo.url')} alt="Logo" className="h-16 max-w-[180px] object-contain mb-2" />
              )}
              <h1 className="text-2xl font-bold uppercase tracking-wider">{getBranding('club.name', 'Club Équestre')}</h1>
              <p className="text-xs text-gray-500">{getBranding('club.subtitle')}</p>
              <p className="text-xs text-gray-500">{getBranding('club.address')} · Tél: {getBranding('club.phone')}</p>
            </div>
            <div className="text-right">
              <h2 className="text-xl font-extrabold text-gray-800">BON D'ACHAT &amp; FACTURE</h2>
              <p className="text-sm font-semibold text-indigo-600">N°: {printInvoice.invoiceNumber}</p>
              <p className="text-xs text-gray-500">Date: {new Date(printInvoice.invoiceDate).toLocaleDateString('fr-DZ')}</p>
            </div>
          </div>

          <div className="mb-6 grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-bold text-gray-700">Fournisseur:</p>
              <p className="text-lg font-semibold">{printInvoice.supplierName}</p>
            </div>
            <div className="text-right">
              <p className="font-bold text-gray-700">Statut de Règlement:</p>
              <p className="text-lg font-semibold">{printInvoice.status === 'Paid' ? 'SOLDÉE' : printInvoice.status === 'PartiallyPaid' ? 'PARTIEL' : 'NON PAYÉE'}</p>
            </div>
          </div>

          <table className="w-full mb-6 text-sm border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-300">
                <th className="p-2 text-left border-r border-gray-300">Produit / Description</th>
                <th className="p-2 text-center border-r border-gray-300">Quantité</th>
                <th className="p-2 text-right border-r border-gray-300">Prix Unitaire</th>
                <th className="p-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {printInvoice.lineItems?.map((li, idx) => (
                <tr key={idx} className="border-b border-gray-200">
                  <td className="p-2 border-r border-gray-200">{li.productName || li.description}</td>
                  <td className="p-2 text-center border-r border-gray-200">{li.quantity}</td>
                  <td className="p-2 text-right border-r border-gray-200">{fmt(li.unitPrice)}</td>
                  <td className="p-2 text-right">{fmt(li.totalPrice)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex justify-end mb-8">
            <div className="w-64 text-sm space-y-1">
              <div className="flex justify-between font-bold border-b pb-1">
                <span>Montant Total:</span>
                <span>{fmt(printInvoice.totalAmount)}</span>
              </div>
              <div className="flex justify-between text-green-700">
                <span>Montant Payé:</span>
                <span>{fmt(printInvoice.paidAmount)}</span>
              </div>
              <div className="flex justify-between text-red-700 font-bold border-t pt-1 text-base">
                <span>Solde Restant Dû:</span>
                <span>{fmt(printInvoice.remainingBalance)}</span>
              </div>
            </div>
          </div>

          {printInvoice.payments?.length > 0 && (
            <div className="mb-6">
              <h3 className="font-bold text-sm mb-2 border-b pb-1">Historique des Règlements (Acomptes):</h3>
              <ul className="text-xs space-y-1">
                {printInvoice.payments.map((p, idx) => (
                  <li key={idx} className="flex justify-between border-b border-gray-100 py-1">
                    <span>{new Date(p.paymentDate).toLocaleDateString('fr-DZ')} ({p.paymentMethod || 'Espèces'})</span>
                    <span className="font-semibold">{fmt(p.amount)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="text-center text-xs text-gray-400 mt-12 border-t pt-4">
            {getBranding('club.footer.note', 'Merci pour votre confiance !')} — Document imprimé le {new Date().toLocaleDateString('fr-DZ')}
          </div>
        </div>
      )}

      {/* Screen View */}
      <div className="print:hidden">
        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>📄 Facturation & Dettes Fournisseurs</h2>
            <p style={{ color: '#64748b', fontSize: 14, margin: '4px 0 0' }}>Gestion des factures, bons d'achat et règlements d'acomptes</p>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button style={styles.btn('green')} onClick={() => setShowCreateModal(true)}>
              <Plus size={16} /> Créer une Facture / Bon d'Achat
            </button>
          </div>
        </div>

        {/* ── Summary Cards ─────────────────────────────────────────────────── */}
        {summary && (
          <div style={styles.summaryGrid}>
            <div style={{ ...styles.card, borderTop: '3px solid #ef4444' }}>
              <div style={styles.cardLabel}>Dettes / À Payer</div>
              <div style={{ ...styles.cardValue, color: '#ef4444' }}>{fmt(summary.totalUnpaid + summary.totalPartiallyPaid)}</div>
            </div>
            <div style={{ ...styles.card, borderTop: '3px solid #f59e0b' }}>
              <div style={styles.cardLabel}>Partiellement Payées</div>
              <div style={{ ...styles.cardValue, color: '#f59e0b' }}>{summary.countByStatus?.PartiallyPaid || 0} fact.</div>
            </div>
            <div style={{ ...styles.card, borderTop: '3px solid #6366f1' }}>
              <div style={styles.cardLabel}>Factures Impayées</div>
              <div style={{ ...styles.cardValue, color: '#6366f1' }}>{summary.countByStatus?.Unpaid || 0}</div>
            </div>
            <div style={{ ...styles.card, borderTop: '3px solid #10b981' }}>
              <div style={styles.cardLabel}>Factures Soldées</div>
              <div style={{ ...styles.cardValue, color: '#10b981' }}>{summary.countByStatus?.Paid || 0}</div>
            </div>
          </div>
        )}

        {/* ── Filters ─────────────────────────────────────────────────────────── */}
        <div style={styles.filterBar}>
          <div style={{ position: 'relative' }}>
            <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
            <input
              style={{ ...styles.input, paddingLeft: 34, width: 220 }}
              placeholder="Rechercher facture..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
            />
          </div>
          <select style={styles.input} value={filterSupplierId} onChange={e => { setFilterSupplierId(e.target.value); setPage(1); }}>
            <option value="">Tous les fournisseurs</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select style={styles.input} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
            {statusOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button style={styles.btn('secondary')} onClick={fetchInvoices}><RefreshCw size={14} /></button>
        </div>

        {/* ── Factures Table (Soldées on Top) ─────────────────────────────────── */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
            <RefreshCw size={30} style={{ animation: 'spin 1s linear infinite', marginBottom: 12 }} />
            <div>Chargement des factures…</div>
          </div>
        ) : invoices.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
            <FileText size={48} style={{ marginBottom: 12, opacity: 0.4 }} />
            <div style={{ fontSize: 16 }}>Aucune facture trouvée</div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}></th>
                  <th style={styles.th}>N° Facture</th>
                  <th style={styles.th}>Fournisseur</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Total</th>
                  <th style={styles.th}>Payé</th>
                  <th style={styles.th}>Reste Dû</th>
                  <th style={styles.th}>Statut</th>
                  <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map(invoice => (
                  <React.Fragment key={invoice.id}>
                    <tr className="hover:bg-gray-50 transition-colors">
                      <td style={styles.td} onClick={() => setExpandedInvoice(expandedInvoice === invoice.id ? null : invoice.id)}>
                        {expandedInvoice === invoice.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </td>
                      <td style={styles.td} onClick={() => setExpandedInvoice(expandedInvoice === invoice.id ? null : invoice.id)}>
                        <strong style={{ color: '#6366f1' }}>{invoice.invoiceNumber}</strong>
                      </td>
                      <td style={styles.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Building2 size={14} color="#94a3b8" /> {invoice.supplierName}
                        </div>
                      </td>
                      <td style={styles.td}>{new Date(invoice.invoiceDate).toLocaleDateString('fr-DZ')}</td>
                      <td style={{ ...styles.td, fontWeight: 700 }}>{fmt(invoice.totalAmount)}</td>
                      <td style={{ ...styles.td, color: '#10b981', fontWeight: 600 }}>{fmt(invoice.paidAmount)}</td>
                      <td style={{ ...styles.td, color: invoice.remainingBalance > 0 ? '#ef4444' : '#10b981', fontWeight: 700 }}>
                        {fmt(invoice.remainingBalance)}
                      </td>
                      <td style={styles.td}><StatusBadge status={invoice.status} /></td>
                      <td style={{ ...styles.td, textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button
                            title="Imprimer / Télécharger la facture"
                            style={{ ...styles.btn('secondary'), padding: '6px 10px', fontSize: 12 }}
                            onClick={() => triggerPrint(invoice)}
                          >
                            <Printer size={13} /> Imprimer
                          </button>
                          {invoice.status !== 'Paid' && invoice.status !== 'Cancelled' && (
                            <button
                              title="Ajouter un paiement / Acompte"
                              style={{ ...styles.btn('primary'), padding: '6px 12px', fontSize: 12 }}
                              onClick={() => openPaymentModal(invoice)}
                            >
                              <CreditCard size={13} /> Régler / Dettes
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded details */}
                    {expandedInvoice === invoice.id && (
                      <tr>
                        <td colSpan="9" style={{ background: '#f8fafc', padding: '16px 24px', borderBottom: '1px solid #e2e8f0' }}>
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20 }}>
                            <div>
                              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <Package size={14} /> Produits Achetés ({invoice.lineItems?.length || 0})
                              </h4>
                              <table style={{ width: '100%', fontSize: 13, background: '#fff', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                                <thead>
                                  <tr style={{ background: '#f1f5f9' }}>
                                    <th style={{ padding: '8px 12px', textAlign: 'left' }}>Produit</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'center' }}>Qté</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right' }}>Prix U.</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'right' }}>Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {invoice.lineItems?.map((li, idx) => (
                                    <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                      <td style={{ padding: '8px 12px', fontWeight: 600 }}>{li.productName || li.description || '-'}</td>
                                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>{li.quantity}</td>
                                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>{fmt(li.unitPrice)}</td>
                                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{fmt(li.totalPrice)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            <div>
                              <h4 style={{ fontSize: 13, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                                <CreditCard size={14} /> Historique des Règlements ({invoice.payments?.length || 0})
                              </h4>
                              {invoice.payments?.length === 0 ? (
                                <div style={{ fontSize: 13, color: '#94a3b8', fontStyle: 'italic' }}>Aucun acompte enregistré</div>
                              ) : (
                                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                                  {invoice.payments.map((p, idx) => (
                                    <li key={idx} style={{ background: '#fff', borderRadius: 8, padding: '10px 14px', marginBottom: 8, border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                                      <div>
                                        <div style={{ fontWeight: 700, color: '#10b981' }}>{fmt(p.amount)}</div>
                                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{new Date(p.paymentDate).toLocaleDateString('fr-DZ')} · {p.paymentMethod || 'Espèces'}</div>
                                      </div>
                                      {p.reference && <div style={{ fontSize: 11, color: '#64748b' }}>Réf: {p.reference}</div>}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', background: '#fafafa', borderTop: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: 13, color: '#64748b' }}>
                  Page <strong>{page}</strong> sur <strong>{totalPages}</strong>
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    style={{ ...styles.btn('secondary'), opacity: page <= 1 ? 0.4 : 1, cursor: page <= 1 ? 'not-allowed' : 'pointer' }}
                  >
                    Précédent
                  </button>
                  <button
                    disabled={page >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    style={{ ...styles.btn('secondary'), opacity: page >= totalPages ? 0.4 : 1, cursor: page >= totalPages ? 'not-allowed' : 'pointer' }}
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Create Invoice Modal */}
        {showCreateModal && (
          <Modal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            title="Créer une Facture / Bon d'Achat"
            size="lg"
          >
            <form onSubmit={handleCreateInvoice} style={{ padding: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
                <div>
                  <label style={styles.label}>Fournisseur *</label>
                  <select
                    style={styles.formInput}
                    value={invoiceForm.supplierId}
                    onChange={e => setInvoiceForm(f => ({ ...f, supplierId: e.target.value }))}
                    required
                  >
                    <option value="">— Choisir fournisseur —</option>
                    {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={styles.label}>Date Facture</label>
                  <input
                    type="date"
                    style={styles.formInput}
                    value={invoiceForm.invoiceDate}
                    onChange={e => setInvoiceForm(f => ({ ...f, invoiceDate: e.target.value }))}
                    required
                  />
                </div>
              </div>

              {/* Line Items */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ ...styles.label, marginBottom: 10 }}>Produits / Lignes d'achat *</label>
                {invoiceForm.lineItems.map((item, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <select
                      style={styles.formInput}
                      value={item.productId}
                      onChange={e => updateLineItem(idx, 'productId', e.target.value)}
                    >
                      <option value="">— Sélectionner un produit —</option>
                      {inventoryItems.map(p => <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>)}
                    </select>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      placeholder="Qté"
                      style={styles.formInput}
                      value={item.quantity}
                      onChange={e => updateLineItem(idx, 'quantity', e.target.value)}
                      required
                    />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Prix unitaire"
                      style={styles.formInput}
                      value={item.unitPrice}
                      onChange={e => updateLineItem(idx, 'unitPrice', e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => removeLineItem(idx)}
                      disabled={invoiceForm.lineItems.length === 1}
                      style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 4 }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addLineItem}
                  style={{ ...styles.btn('secondary'), padding: '6px 12px', fontSize: 12, marginTop: 4 }}
                >
                  <Plus size={13} /> Ajouter une ligne
                </button>
              </div>

              <div style={styles.totalBar}>
                Total Facture : {fmt(invoiceTotal)}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" style={styles.btn('secondary')} onClick={() => setShowCreateModal(false)}>Annuler</button>
                <button type="submit" style={styles.btn('green')} disabled={submitting}>
                  {submitting ? 'Enregistrement...' : 'Enregistrer la facture'}
                </button>
              </div>
            </form>
          </Modal>
        )}

        {/* Add Payment / Acompte Modal */}
        {showPaymentModal && selectedInvoice && (
          <Modal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            title={`Régler la dette — ${selectedInvoice.invoiceNumber}`}
            size="sm"
          >
            <form onSubmit={handleAddPayment} style={{ padding: 20 }}>
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: 12, marginBottom: 16, border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#64748b' }}>
                  <span>Fournisseur</span><strong>{selectedInvoice.supplierName}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, color: '#ef4444', marginTop: 4 }}>
                  <span>Reste Dû</span><span>{fmt(selectedInvoice.remainingBalance)}</span>
                </div>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Montant Versé / Acompte (DZD) *</label>
                <input
                  type="number"
                  min="0.01"
                  max={selectedInvoice.remainingBalance}
                  step="0.01"
                  style={styles.formInput}
                  value={paymentForm.amount}
                  onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Mode de Règlement</label>
                <select
                  style={styles.formInput}
                  value={paymentForm.paymentMethod}
                  onChange={e => setPaymentForm(f => ({ ...f, paymentMethod: e.target.value }))}
                >
                  <option value="">— Choisir mode —</option>
                  {paymentMethods.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Référence (N° Chèque / Virement)</label>
                <input
                  type="text"
                  style={styles.formInput}
                  value={paymentForm.reference}
                  onChange={e => setPaymentForm(f => ({ ...f, reference: e.target.value }))}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <button type="button" style={styles.btn('secondary')} onClick={() => setShowPaymentModal(false)}>Annuler</button>
                <button type="submit" style={styles.btn('primary')} disabled={submitting}>
                  {submitting ? 'Enregistrement...' : 'Valider le Règlement'}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default SupplierInvoiceManagement;
